/**
 * quickSearchLogic - the pure half of the CRM's quick search, the Ctrl+K palette
 * (docs/plans/crm-report-builder-search-collab-plan.md A2.2 to A2.4 and B2.5; WP Q-3):
 *
 *   - CRM_PAGES: every CRM page the palette offers, with the gate the sidebar (or the settings
 *     rail) applies to it, and matchPages over them;
 *   - buildSections: Recently viewed (an empty box), then Leads, Studies and Pages (a search);
 *   - the flat keyboard model: flattenSections, moveActive (wraps, skips headers), keepActive;
 *   - the recently viewed store: per person, on this device, lead ids only (decision Q4 a);
 *   - the flags on a lead row (leadFlags) and the defensive reading of the api's answers.
 *
 * Every word the palette shows is decided here, never ad hoc in a component (the recruitLogic.ts
 * rule). React-free at the top level and api-free: the only runtime imports are types.ts's two label
 * helpers and snoozeLabel from components/SnoozeButton.tsx (whose own imports are react,
 * lucide-react and the ui kit), so this module's tests need no mocks. browserRecentStore is the one
 * function that touches the browser, and only when called.
 */
import { snoozeLabel } from '../components/SnoozeButton';
import { contactName, studyInternalLabel } from '../types';
import type {
  QuickLead,
  QuickLeadFlag,
  QuickOption,
  QuickPage,
  QuickPageGates,
  QuickQueryKind,
  QuickRow,
  QuickSearchResponse,
  QuickSearchViewState,
  QuickSection,
  QuickSectionId,
  QuickStudy,
  RecentStore,
} from './types';

// ---------------------------------------------------------------------------------------------
// The numbers (A2.4, B2.2, B2.5)
// ---------------------------------------------------------------------------------------------

/** Ask the server this long after the last keystroke; each keystroke also aborts the request before. */
export const DEBOUNCE_MS = 150;
/** Fewer characters than this (after trimming) is not a search: the box shows the recently viewed leads. */
export const MIN_CHARS = 2;
/** The server trims and clips the text here (crmQuickSearch QUICK_QUERY_MAX); the client does the same. */
export const QUICK_QUERY_MAX = 100;
/** At most eight leads, five studies and six pages at a time (A2.4). */
export const LEAD_LIMIT = 8;
export const STUDY_LIMIT = 5;
export const PAGE_LIMIT = 6;
/** Recently viewed: the last eight leads opened (A2.4). */
export const RECENT_LIMIT = 8;
/** GET /quick-search/recent reads at most ten ids (crmQuickSearch RECENT_MAX). */
export const RECENT_RESOLVE_MAX = 10;
/** The store key's prefix; the signed-in user's id completes it (recentKey). */
export const RECENT_KEY_PREFIX = 'crm.recentLeads.v1:';

// ---------------------------------------------------------------------------------------------
// The words
// ---------------------------------------------------------------------------------------------

export const QUICK_WORDS = {
  /** The top-bar box from sm: up, beside the shortcut hint. */
  trigger: 'Search leads',
  /** The palette's input. */
  placeholder: 'Search leads by name, phone or email',
  recent: 'Recently viewed',
  leads: 'Leads',
  studies: 'Studies',
  pages: 'Pages',
  openStudy: 'Open study',
  recruitmentList: 'Recruitment list',
  seeAll: 'See all in Patients',
  /** An empty box and nothing opened yet on this device. */
  startTyping: 'Type a name, phone number or email to find a lead.',
  unnamedStudy: 'Untitled study',
} as const;

const SECTION_TITLES: Record<QuickSectionId, string> = {
  recent: QUICK_WORDS.recent,
  leads: QUICK_WORDS.leads,
  studies: QUICK_WORDS.studies,
  pages: QUICK_WORDS.pages,
};

/** "Ctrl K", or "Cmd K" on a Mac (the trigger's hint; both keys open the palette everywhere). */
export function shortcutHint(isMac: boolean): string {
  return isMac ? 'Cmd K' : 'Ctrl K';
}

/** A Mac, iPhone or iPad. Pass navigator.platform (or navigator.userAgent when that is empty). */
export function isMacPlatform(platform: string | null | undefined): boolean {
  return /mac|iphone|ipad|ipod/i.test(platform || '');
}

/** The line under a search that found nothing at all. */
export function noMatchWords(text: string): string {
  return `No leads, studies or pages match "${text}".`;
}

// ---------------------------------------------------------------------------------------------
// The text
// ---------------------------------------------------------------------------------------------

/** The text as the server reads it: trimmed, then clipped to 100 characters. */
export function clipQuery(query: unknown): string {
  return typeof query === 'string' ? query.trim().slice(0, QUICK_QUERY_MAX) : '';
}

/** 'recent' below MIN_CHARS (the box shows the recently viewed leads), else 'search'. */
export function queryMode(query: unknown): 'recent' | 'search' {
  return clipQuery(query).length < MIN_CHARS ? 'recent' : 'search';
}

/** Only digits, spaces and ( ) . + - : a phone number being typed (B2.2 parseQuickQuery's phone rule). */
const PHONE_TEXT_RE = /^[\d\s().+-]+$/;

/** Lower-case words of letters and digits: "Settings > Forms" gives settings, forms. */
function wordsOf(s: string): string[] {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

// ---------------------------------------------------------------------------------------------
// The CRM pages (B2.5): the routes of CRMApp.tsx, gated exactly as the sidebar and the settings
// rail gate them. The test reads Sidebar.tsx, SettingsLayout.tsx and CRMApp.tsx and holds this
// list to them both ways, so a new sidebar item or route that is missing here fails CI.
// ---------------------------------------------------------------------------------------------

export const CRM_PAGES: readonly QuickPage[] = [
  // The sidebar (components/Sidebar.tsx), in its order.
  { path: '/crm', label: 'Today', group: null, gate: 'all', keywords: ['home', 'worklist', 'due today', 'tasks'] },
  { path: '/crm/outreach', label: 'Outreach lists', group: null, gate: 'outreach', keywords: ['call lists', 'my lists', 'assigned'] },
  { path: '/crm/calendar', label: 'Calendar', group: null, gate: 'all', keywords: ['appointments', 'schedule', 'callbacks', 'visits'] },
  // Built in the same run as the palette (plan B3.4; X-4 adds the route): open to everyone.
  { path: '/crm/mentions', label: 'Mentions', group: null, gate: 'all', keywords: ['mentioned', 'at mentions', 'replies to my notes', 'notifications'] },
  { path: '/crm/inbox', label: 'Lead Inbox', group: null, gate: 'all', keywords: ['leads', 'new leads', 'triage', 'contacts'] },
  { path: '/crm/active', label: 'Conversations', group: null, gate: 'all', keywords: ['messages', 'texts', 'sms', 'needs reply', 'replies'] },
  { path: '/crm/awaiting', label: 'No reply yet', group: null, gate: 'all', keywords: ['awaiting reply', 'waiting', 'no response', 'unanswered'] },
  { path: '/crm/recruit', label: 'Recruit', group: null, gate: 'recruit', keywords: ['recruitment lists', 'sheet', 'recruiting'] },
  { path: '/crm/by-study', label: 'By study', group: null, gate: 'all', keywords: ['studies', 'roster', 'pipeline'] },
  { path: '/crm/safety-net', label: 'Safety net', group: null, gate: 'all', keywords: ['left behind', 'no next step', 'catches'] },
  { path: '/crm/database', label: 'Patients', group: null, gate: 'all', keywords: ['database', 'all leads', 'all contacts', 'archived leads'] },
  { path: '/crm/search', label: 'Search', group: null, gate: 'all', keywords: ['cohort', 'saved searches', 'funnel search', 'filters'] },
  { path: '/crm/agent', label: 'Agent console', group: null, gate: 'all', keywords: ['texting agent', 'ai agent', 'drafts', 'escalations', 'agent reviews'] },
  { path: '/crm/phone', label: 'Phone', group: null, gate: 'all', keywords: ['dialer', 'dial pad', 'voicemails', 'calls', 'recent calls', 'parked calls', 'greeting'] },
  { path: '/crm/fax', label: 'Fax', group: null, gate: 'all', keywords: ['faxes', 'send fax', 'fax history'] },
  { path: '/crm/reports', label: 'Reports', group: null, gate: 'all', keywords: ['analytics', 'funnel', 'conversion', 'screening', 'dispositions', 'calls', 'productivity', 'metrics'] },
  { path: '/crm/help', label: 'Help', group: null, gate: 'all', keywords: ['handbook', 'guide', 'how to', 'quick start', 'admin guide'] },
  // The settings rail (pages/settings/SettingsLayout.tsx), in its order. The sidebar's one
  // Settings item opens /crm/settings, which redirects to Studies; these are its pages.
  { path: '/crm/settings/studies', label: 'Studies', group: 'Settings', gate: 'all', keywords: ['study', 'study setup', 'protocols', 'trials', 'prescreen'] },
  { path: '/crm/settings/forms', label: 'Forms', group: 'Settings', gate: 'all', keywords: ['landing forms', 'questionnaires', 'prescreen forms', 'survey'] },
  { path: '/crm/settings/profile-keys', label: 'Field tags', group: 'Settings', gate: 'all', keywords: ['profile keys', 'answer keys', 'fields'] },
  { path: '/crm/settings/interest-lists', label: 'Future-study lists', group: 'Settings', gate: 'all', keywords: ['interest lists', 'future studies', 'waitlist'] },
  { path: '/crm/settings/flows', label: 'Flows', group: 'Settings', gate: 'all', keywords: ['automations', 'sequences', 'cadence', 'workflows'] },
  { path: '/crm/settings/templates', label: 'Global templates', group: 'Settings', gate: 'admin', keywords: ['message templates', 'text templates', 'email templates'] },
  { path: '/crm/settings/prompts', label: 'AI Prompts', group: 'Settings', gate: 'admin', keywords: ['prompt editor'] },
  { path: '/crm/settings/release-content', label: 'Release wording', group: 'Settings', gate: 'admin', keywords: ['records release', 'authorization wording', 'hipaa release'] },
  { path: '/crm/settings/registry-backfill', label: 'Registry backfill', group: 'Settings', gate: 'admin', keywords: ['crio study ids', 'registry'] },
  { path: '/crm/settings/clinician-portal', label: 'Clinician portal', group: 'Settings', gate: 'admin', keywords: ['referrals', 'referring clinicians', 'portal'] },
  { path: '/crm/settings/agent', label: 'Texting agent', group: 'Settings', gate: 'admin', keywords: ['agent settings', 'ai texting'] },
  { path: '/crm/settings/booking', label: 'Booking hours', group: 'Settings', gate: 'admin', keywords: ['availability', 'scheduling hours', 'office hours'] },
  { path: '/crm/settings/ad-campaigns', label: 'Ad campaigns', group: 'Settings', gate: 'admin', keywords: ['meta ads', 'facebook ads', 'campaign mapping'] },
  { path: '/crm/settings/system', label: 'System', group: 'Settings', gate: 'super_admin', keywords: ['connections', 'integrations', 'status', 'health'] },
  // Pages the sidebar reaches through another page: the search builder's static route, the two
  // Fax tabs that were pages of their own (the /records and /provider-offices redirects), and the
  // Reports tab this run adds (plan B4.6: ?tab=custom, open to every CRM member).
  { path: '/crm/search/new', label: 'New search', group: 'Search', gate: 'all', keywords: ['cohort builder', 'new cohort'] },
  { path: '/crm/fax?tab=unmatched', label: 'Unmatched', group: 'Fax', gate: 'all', keywords: ['records', 'unmatched faxes', 'triage', 'inbound faxes'] },
  { path: '/crm/fax?tab=offices', label: 'Provider offices', group: 'Fax', gate: 'all', keywords: ['offices', 'doctors', 'fax directory', 'providers'] },
  { path: '/crm/reports?tab=custom', label: 'Custom', group: 'Reports', gate: 'all', keywords: ['report builder', 'custom reports', 'build a report', 'saved reports'] },
];

/** "Settings > Forms", or the label alone for a top-level page. */
export function pageTitle(page: Pick<QuickPage, 'label' | 'group'>): string {
  return page.group ? `${page.group} > ${page.label}` : page.label;
}

/**
 * Whether the viewer is offered the page. The role gates follow useIsCRMAdmin and the rail's
 * super_admin check; outreach defaults to isAdmin (the sidebar shows admins the item whenever the
 * api serves lists; anyone else only while on a list, which only the shared nav counts know, so the
 * caller passes showOutreachNav's answer); recruit defaults to shown (the recruitment list is served
 * by the same deploy as quick search; pass false to hide it as the sidebar does while its probe
 * fails).
 */
export function pageAllowed(page: Pick<QuickPage, 'gate'>, isAdmin: boolean, gates: QuickPageGates = {}): boolean {
  switch (page.gate) {
    case 'all':
      return true;
    case 'admin':
      return isAdmin;
    case 'super_admin':
      return gates.isSuperAdmin === true;
    case 'outreach':
      return gates.outreach === undefined ? isAdmin : gates.outreach === true;
    case 'recruit':
      return gates.recruit !== false;
    default:
      return false;
  }
}

/**
 * How well a page matches: 0 unless every typed word starts a word of the page's label, group or
 * keywords; 3 when the label (or "group label") starts with the text, 2 when every word is found in
 * the label or group, 1 when a keyword was needed.
 */
function pageScore(page: QuickPage, words: readonly string[], text: string): number {
  const label = wordsOf(page.label);
  const title = page.group ? wordsOf(page.group).concat(label) : label;
  const pool = title.concat(...page.keywords.map(wordsOf));
  const found = (within: readonly string[]) => words.every((w) => within.some((x) => x.startsWith(w)));
  if (!found(pool)) return 0;
  if (label.join(' ').startsWith(text) || title.join(' ').startsWith(text)) return 3;
  return found(title) ? 2 : 1;
}

/**
 * The pages to offer for the text, best first (ties keep CRM_PAGES order), at most six, only those
 * the viewer is offered. Nothing below MIN_CHARS, and nothing for an email or a phone number being
 * typed (those are lead searches).
 */
export function matchPages(query: string, isAdmin: boolean, gates: QuickPageGates = {}): QuickPage[] {
  const typed = clipQuery(query);
  if (typed.length < MIN_CHARS || typed.includes('@') || PHONE_TEXT_RE.test(typed)) return [];
  const words = wordsOf(typed);
  if (!words.length) return [];
  const text = words.join(' ');
  const scored: { page: QuickPage; score: number; index: number }[] = [];
  CRM_PAGES.forEach((page, index) => {
    if (!pageAllowed(page, isAdmin, gates)) return;
    const score = pageScore(page, words, text);
    if (score > 0) scored.push({ page, score, index });
  });
  scored.sort((a, b) => b.score - a.score || a.index - b.index);
  return scored.slice(0, PAGE_LIMIT).map((s) => s.page);
}

// ---------------------------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The lead page. */
export function leadHref(id: string): string {
  return `/crm/contacts/${encodeURIComponent(id)}`;
}

/** The study page (the study hub; open to every CRM member). */
export function studyHref(id: string): string {
  return `/crm/settings/studies/${encodeURIComponent(id)}`;
}

/** The study's recruitment list (recruit/RecruitHome.tsx recruitSheetPath builds the same path). */
export function recruitSheetHref(slug: string): string {
  return `/crm/recruit/${encodeURIComponent(slug)}`;
}

/** Where "See all in Patients" goes; the text rides router state (QuickSeeAllState), never the URL. */
export const PATIENTS_PATH = '/crm/database';

// ---------------------------------------------------------------------------------------------
// Recently viewed: per person, on this device, lead ids only (A2.4; decision Q4 a)
// ---------------------------------------------------------------------------------------------

/** A lead id in lower case, or null: nothing but a UUID is ever kept (never a name or a number). */
function leadIdOf(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return UUID_RE.test(s) ? s.toLowerCase() : null;
}

/** Distinct lead ids in order (the first copy wins), junk dropped, at most `max`. */
function cleanIds(list: unknown, max: number): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const v of list) {
    if (out.length >= max) break;
    const id = leadIdOf(v);
    if (id && !out.includes(id)) out.push(id);
  }
  return out;
}

/** The person's own key: a shared front-desk browser keeps one list per person (outreachNotice.ts shape). */
export function recentKey(userId: string): string {
  return `${RECENT_KEY_PREFIX}${userId}`;
}

/** The stored list, junk-tolerant: bad JSON, another shape, names or numbers read as nothing. */
export function parseRecent(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    return cleanIds(JSON.parse(raw), RECENT_LIMIT);
  } catch {
    return [];
  }
}

/** The value to store: a JSON array of at most eight lead ids. */
export function serializeRecent(ids: readonly string[]): string {
  return JSON.stringify(cleanIds(ids, RECENT_LIMIT));
}

/** The list after opening a lead: that id first, its older copy dropped, at most eight. */
export function pushRecent(list: readonly string[], id: string): string[] {
  const fresh = leadIdOf(id);
  return cleanIds(fresh ? [fresh, ...list] : [...list], RECENT_LIMIT);
}

/** The lead id of a lead page path (/crm/contacts/<uuid>, a query or hash allowed), else null. X-4 records it. */
export function recentIdFromPath(path: string | null | undefined): string | null {
  if (typeof path !== 'string') return null;
  const bare = path.split(/[?#]/)[0];
  const match = /^\/crm\/contacts\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i.exec(bare);
  return match ? match[1].toLowerCase() : null;
}

/** window.localStorage, or null where the browser refuses it (a private window, a sandboxed frame). */
export function browserRecentStore(): RecentStore | null {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** The person's list; nothing without a signed-in user or a store (never a shared key). */
export function readRecent(store: RecentStore | null | undefined, userId: string | null | undefined): string[] {
  if (!store || !userId) return [];
  try {
    return parseRecent(store.getItem(recentKey(userId)));
  } catch {
    return [];
  }
}

/** Keep the person's list; a refusing store (quota, private mode) just keeps nothing. */
export function writeRecent(
  store: RecentStore | null | undefined,
  userId: string | null | undefined,
  ids: readonly string[],
): void {
  if (!store || !userId) return;
  try {
    store.setItem(recentKey(userId), serializeRecent(ids));
  } catch {
    /* the list just is not kept */
  }
}

/**
 * Record a visit to `path` when it is a lead page: pushRecent and keep. Answers the new list, or null
 * when nothing was recorded (not a lead page, no user, no store). The CRMRoutes effect calls it (X-4).
 */
export function recordRecentView(
  store: RecentStore | null | undefined,
  userId: string | null | undefined,
  path: string | null | undefined,
): string[] | null {
  const id = recentIdFromPath(path);
  if (!id || !store || !userId) return null;
  const next = pushRecent(readRecent(store, userId), id);
  writeRecent(store, userId, next);
  return next;
}

/** The ids GET /quick-search/recent is asked for: lead ids only, each once, at most ten. */
export function recentRequestIds(ids: readonly string[]): string[] {
  return cleanIds(ids, RECENT_RESOLVE_MAX);
}

/**
 * The list to keep after GET /quick-search/recent answered for `requested`: the answer's ids in its
 * order (a merged lead already replaced by its kept record, a missing or hidden one dropped, A2.4),
 * behind any id recorded since the request went out (in `current` but not asked about; another tab
 * may have opened a lead meanwhile). Call it only on an answer: a failed call keeps the list as is.
 */
export function recentAfterResolve(
  requested: readonly string[],
  current: readonly string[],
  leads: readonly Pick<QuickLead, 'id'>[],
): string[] {
  const asked = new Set(recentRequestIds(requested));
  const since = cleanIds(current, RECENT_LIMIT).filter((id) => !asked.has(id));
  return cleanIds([...since, ...leads.map((l) => l.id)], RECENT_LIMIT);
}

// ---------------------------------------------------------------------------------------------
// Reading the api's answers (defensive: a missing field is null, a malformed row is dropped)
// ---------------------------------------------------------------------------------------------

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const record = (v: unknown): Record<string, unknown> | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;

const QUERY_KINDS: readonly QuickQueryKind[] = ['email', 'phone', 'name', 'empty'];

/**
 * One lead row, or null when it has no lead id. The study titles are read from the flat
 * study_title / study_internal_title keys (types.ts); a nested `study: { title, internal_title }`
 * is read too, so a server that nests them renders the same.
 */
export function normalizeLead(raw: unknown): QuickLead | null {
  const r = record(raw);
  if (!r || typeof r.id !== 'string' || !UUID_RE.test(r.id)) return null;
  const study = record(r.study);
  return {
    id: r.id,
    full_name: str(r.full_name),
    phone_e164: str(r.phone_e164),
    email: str(r.email),
    lifecycle_status: str(r.lifecycle_status),
    owner_user_id: str(r.owner_user_id),
    primary_study_id: str(r.primary_study_id),
    archived_at: str(r.archived_at),
    snoozed_until: str(r.snoozed_until),
    sms_opt_out: r.sms_opt_out === true,
    last_activity_at: str(r.last_activity_at),
    study_title: str(r.study_title) ?? (study ? str(study.title) : null),
    study_internal_title: str(r.study_internal_title) ?? (study ? str(study.internal_title) : null),
    do_not_contact: r.do_not_contact === true,
  };
}

/** One study row, or null when it has no study id. */
export function normalizeStudy(raw: unknown): QuickStudy | null {
  const r = record(raw);
  if (!r || typeof r.id !== 'string' || !UUID_RE.test(r.id)) return null;
  return {
    id: r.id,
    slug: str(r.slug),
    title: str(r.title),
    internal_title: str(r.internal_title),
    status: str(r.status),
    has_recruitment_list: r.has_recruitment_list === true,
  };
}

function distinctBy<T extends { id: string }>(rows: (T | null)[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const row of rows) {
    if (!row || seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
  }
  return out;
}

/** A `leads` array, each lead once, in the server's order. */
export function normalizeLeads(raw: unknown): QuickLead[] {
  return Array.isArray(raw) ? distinctBy(raw.map(normalizeLead)) : [];
}

/** A `studies` array, each study once, in the server's order. */
export function normalizeStudies(raw: unknown): QuickStudy[] {
  return Array.isArray(raw) ? distinctBy(raw.map(normalizeStudy)) : [];
}

/** The answer of an empty or too-short text (the server's, without asking it). */
export function emptyQuickResponse(): QuickSearchResponse {
  return { leads: [], studies: [], more: false, interpreted: { kind: 'empty', digits_count: 0 } };
}

/** POST /quick-search's answer, whatever arrived. */
export function normalizeQuickResponse(raw: unknown): QuickSearchResponse {
  const r = record(raw) ?? {};
  const i = record(r.interpreted) ?? {};
  const kind = QUERY_KINDS.find((k) => k === i.kind) ?? 'empty';
  const digits = typeof i.digits_count === 'number' && Number.isFinite(i.digits_count) && i.digits_count > 0
    ? Math.floor(i.digits_count)
    : 0;
  return {
    leads: normalizeLeads(r.leads),
    studies: normalizeStudies(r.studies),
    more: r.more === true,
    interpreted: { kind, digits_count: digits },
  };
}

// ---------------------------------------------------------------------------------------------
// A lead row: the name, the study and the flags (A2.2)
// ---------------------------------------------------------------------------------------------

/** The lead's name, or "(no name)" (types.ts contactName's placeholder). */
export function leadName(lead: Pick<QuickLead, 'full_name'>): string {
  return contactName({ full_name: lead.full_name?.trim() || null, first_name: null, last_name: null });
}

/** The lead's study as a recruiter reads it (internal title, else title); '' when none. */
export function leadStudyLabel(lead: Pick<QuickLead, 'study_title' | 'study_internal_title'>): string {
  return studyInternalLabel({ title: lead.study_title, internal_title: lead.study_internal_title });
}

/** A study's name as a recruiter reads it, or "Untitled study". */
export function studyName(study: Pick<QuickStudy, 'title' | 'internal_title' | 'slug'>): string {
  return studyInternalLabel(study) || QUICK_WORDS.unnamedStudy;
}

/**
 * The flags that matter before a call, in A2.2's order: Archived, Snoozed until <time> (a future
 * snooze only: components/SnoozeButton's isActiveSnooze rule at `now`, its snoozeLabel words),
 * Opted out, Do not contact. The words and tones are the recruitment list's (sheet FlagsCell).
 */
export function leadFlags(
  lead: Pick<QuickLead, 'archived_at' | 'snoozed_until' | 'sms_opt_out' | 'do_not_contact'>,
  now: Date | number = Date.now(),
): QuickLeadFlag[] {
  const nowMs = typeof now === 'number' ? now : now.getTime();
  const flags: QuickLeadFlag[] = [];
  if (lead.archived_at) {
    flags.push({ key: 'archived', label: 'Archived', tone: 'gray', title: 'Archived: off the daily lists' });
  }
  const until = lead.snoozed_until;
  if (until) {
    const t = new Date(until).getTime();
    if (!Number.isNaN(t) && t > nowMs) {
      flags.push({
        key: 'snoozed',
        label: `Snoozed until ${snoozeLabel(until)}`,
        tone: 'primary',
        title: 'Snoozed: a text from them wakes it early',
      });
    }
  }
  if (lead.sms_opt_out) {
    flags.push({ key: 'opted_out', label: 'Opted out', tone: 'red', title: 'Texted STOP: texts are off' });
  }
  if (lead.do_not_contact) {
    flags.push({ key: 'do_not_contact', label: 'Do not contact', tone: 'red', title: 'Asked us not to contact them' });
  }
  return flags;
}

// ---------------------------------------------------------------------------------------------
// The sections (B2.5)
// ---------------------------------------------------------------------------------------------

function leadOption(lead: QuickLead, section: 'recent' | 'leads'): QuickOption {
  return { kind: 'lead', key: `${section === 'recent' ? 'recent' : 'lead'}:${lead.id}`, href: leadHref(lead.id), lead };
}

function studyOptions(study: QuickStudy): QuickOption[] {
  const options: QuickOption[] = [
    { kind: 'study', key: `study:${study.id}`, href: studyHref(study.id), study, target: 'study', label: QUICK_WORDS.openStudy },
  ];
  if (study.has_recruitment_list && study.slug) {
    options.push({
      kind: 'study',
      key: `recruit:${study.id}`,
      href: recruitSheetHref(study.slug),
      study,
      target: 'recruit',
      label: QUICK_WORDS.recruitmentList,
    });
  }
  return options;
}

function sectionOf(id: QuickSectionId, options: QuickOption[]): QuickSection[] {
  return options.length ? [{ id, title: SECTION_TITLES[id], options }] : [];
}

/**
 * What the palette shows. Below MIN_CHARS: Recently viewed (at most eight). A search: Leads (at most
 * eight, in the server's order, then "See all in Patients" when there were more), Studies (at most
 * five, each "Open study" and, when it has one, "Recruitment list"), then Pages (matchPages). A
 * section with nothing in it is left out; a lead or study sent twice is shown once.
 */
export function buildSections(state: QuickSearchViewState): QuickSection[] {
  const text = clipQuery(state.query);
  if (text.length < MIN_CHARS) {
    const recent = distinctBy([...(state.recent || [])]).slice(0, RECENT_LIMIT);
    return sectionOf('recent', recent.map((lead) => leadOption(lead, 'recent')));
  }
  const result = state.result;
  const leads = result ? distinctBy([...result.leads]).slice(0, LEAD_LIMIT) : [];
  const leadOptions = leads.map((lead) => leadOption(lead, 'leads'));
  if (leadOptions.length && result?.more) {
    leadOptions.push({ kind: 'see_all', key: 'see-all', href: PATIENTS_PATH, label: QUICK_WORDS.seeAll, state: { quickSearch: text } });
  }
  const studies = result ? distinctBy([...result.studies]).slice(0, STUDY_LIMIT) : [];
  const studyRows: QuickOption[] = [];
  for (const study of studies) studyRows.push(...studyOptions(study));
  const pages = matchPages(text, state.isAdmin, state.gates).map(
    (page): QuickOption => ({ kind: 'page', key: `page:${page.path}`, href: page.path, page }),
  );
  return [...sectionOf('leads', leadOptions), ...sectionOf('studies', studyRows), ...sectionOf('pages', pages)];
}

/**
 * The line to show when there are no sections: the start-typing hint for an empty box with nothing
 * recently viewed, the no-match line once a search has answered, and null while something is still
 * loading (or when there are sections).
 */
export function emptyMessage(state: QuickSearchViewState, sections: readonly QuickSection[]): string | null {
  if (sections.length) return null;
  if (queryMode(state.query) === 'recent') return state.recent === null ? null : QUICK_WORDS.startTyping;
  return state.result ? noMatchWords(clipQuery(state.query)) : null;
}

// ---------------------------------------------------------------------------------------------
// The flat keyboard model (B2.5): one list of headers and options; the arrows move between
// options only, wrapping at both ends.
// ---------------------------------------------------------------------------------------------

/** Every header, then its options, in display order. */
export function flattenSections(sections: readonly QuickSection[]): QuickRow[] {
  const rows: QuickRow[] = [];
  for (const s of sections) {
    rows.push({ type: 'header', key: `header:${s.id}`, section: s.id, title: s.title });
    for (const option of s.options) rows.push({ type: 'option', key: option.key, section: s.id, option });
  }
  return rows;
}

function optionIndexes(rows: readonly QuickRow[]): number[] {
  const out: number[] = [];
  rows.forEach((row, i) => {
    if (row.type === 'option') out.push(i);
  });
  return out;
}

/** The first option's index, or -1 when there is none. */
export function firstOptionIndex(rows: readonly QuickRow[]): number {
  const options = optionIndexes(rows);
  return options.length ? options[0] : -1;
}

/** The last option's index, or -1 when there is none. */
export function lastOptionIndex(rows: readonly QuickRow[]): number {
  const options = optionIndexes(rows);
  return options.length ? options[options.length - 1] : -1;
}

/**
 * The index of the option `delta` options away from `from` (ArrowDown +1, ArrowUp -1), skipping
 * headers and wrapping at both ends. With nothing active (-1, a header or a stale index) Down starts
 * at the first option and Up at the last. -1 when there are no options.
 */
export function moveActive(rows: readonly QuickRow[], from: number, delta: number): number {
  const options = optionIndexes(rows);
  const n = options.length;
  if (!n) return -1;
  const step = Math.trunc(delta) || 0;
  let pos = options.indexOf(from);
  if (pos === -1) {
    if (step === 0) return options[0];
    pos = step > 0 ? -1 : n;
  }
  return options[(((pos + step) % n) + n) % n];
}

/** After the rows change: the option with the same key if it is still there, else the first option. */
export function keepActive(rows: readonly QuickRow[], key: string | null | undefined): number {
  if (key) {
    const i = rows.findIndex((row) => row.type === 'option' && row.key === key);
    if (i !== -1) return i;
  }
  return firstOptionIndex(rows);
}

/** The option at an index, or null (a header, or out of range). */
export function optionAt(rows: readonly QuickRow[], index: number): QuickOption | null {
  const row = rows[index];
  return row && row.type === 'option' ? row.option : null;
}

/** The DOM id of an option (aria-activedescendant): its key with anything but letters, digits, _ and - made a -. */
export function optionDomId(key: string): string {
  return `quick-search-option-${key.replace(/[^A-Za-z0-9_-]/g, '-')}`;
}

// ---------------------------------------------------------------------------------------------
// The shortcut (decision Q2 a)
// ---------------------------------------------------------------------------------------------

/**
 * Ctrl+K or Cmd+K (either, on any platform; KeyK too, for a keyboard whose K types another letter),
 * never with Alt or Shift (Ctrl+Shift+K is a browser console). "/" is never the palette's key: it
 * stays with the recruitment list's grid and is typed constantly in notes.
 */
export function isQuickSearchHotkey(e: {
  key?: string;
  code?: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
}): boolean {
  if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return false;
  return (typeof e.key === 'string' && e.key.toLowerCase() === 'k') || e.code === 'KeyK';
}
